import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Plain small label above a title or section (entity type, group name). */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('text-sm font-medium text-zinc-400', className)}>{children}</div>;
}

interface PageHeaderProps {
  title: ReactNode;
  /** Entity type / section label above the title. */
  eyebrow?: ReactNode;
  /** Status badge / key facts under the title — keep to one signal. */
  meta?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, eyebrow, meta, actions, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-5 flex flex-wrap items-start gap-x-4 gap-y-2', className)}>
      <div className="min-w-0">
        {eyebrow ? <Eyebrow className="mb-0.5">{eyebrow}</Eyebrow> : null}
        <h1 className="truncate text-lg font-semibold text-zinc-50">{title}</h1>
        {meta ? <div className="mt-1.5 flex flex-wrap items-center gap-2">{meta}</div> : null}
      </div>
      {/* Wraps below `sm`: headers carrying up to eight buttons cannot fit a
          phone on one row, and the buttons themselves are `whitespace-nowrap`,
          so without a wrap they run off the page instead of folding. */}
      {actions ? (
        <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
      ) : null}
    </header>
  );
}
